import React, {useEffect, useState} from 'react';
import {
  ActivityIndicator,
  FlatList,
  Platform,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';
import {observer} from 'mobx-react-lite';

import useIsLoggedIn from '../../hooks/useIsLoggedIn';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import Button from '../ui-controls/Button';
import Text from '../ui-controls/Text';

const TimeSlots = observer(() => {
  const {cartStore} = useStores();
  const isLoggedIn = useIsLoggedIn();
  const [open, setOpen] = useState(false);

  const todayString = new Date().toLocaleDateString();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowString = tomorrow.toLocaleDateString();

  const type = cartStore.isPickup ? 'Pickup' : 'Delivery';

  // These can be loaded right away, as long as we're logged in
  useEffect(() => {
    if (cartStore.isPickup && isLoggedIn) {
      cartStore.loadPickupSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartStore.isPickup, isLoggedIn]);

  useEffect(() => {
    if (cartStore.isDelivery && cartStore.deliveryRegion?.id) {
      cartStore.loadDeliverySlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartStore.isDelivery, cartStore.deliveryRegion]);

  const renderDayTile = ({item}) => {
    const day = new Date(item);
    const itemLocale = day.toLocaleDateString();
    let topString;
    let bottomString = day.toLocaleDateString('en-AU', {weekday: 'short'});

    switch (itemLocale) {
      case todayString:
        topString = 'Today';
        break;
      case tomorrowString:
        topString = 'Tomorrow';
        break;
      default:
        topString = day.toLocaleDateString('en-AU', {
          month: 'short',
          day: 'numeric',
        });
        break;
    }
    return (
      <Pressable
        onPress={() => cartStore.selectDay(item)}
        style={[
          styles.dayTile,
          cartStore.selectedDay === item ? styles.dayTileSelected : null,
        ]}>
        <Text
          style={[
            styles.dayTileUpper,
            cartStore.selectedDay === item ? styles.dayTileSelected : null,
          ]}>
          {topString}
        </Text>
        <Text
          style={[
            styles.dayTileLower,
            cartStore.selectedDay === item ? styles.dayTileSelected : null,
          ]}>
          {bottomString}
        </Text>
      </Pressable>
    );
  };

  const renderDays = () => {
    return (
      <FlatList
        data={cartStore.days}
        renderItem={renderDayTile}
        horizontal
        style={[styles.verticalSpace, styles.daysRow]}
        ItemSeparatorComponent={() => (
          <View style={styles.horizontalSeparator} />
        )}
      />
    );
  };

  const selectTime = item => {
    cartStore.selectSlot(item);
    setOpen(false);
  };

  const renderTimeTile = item => {
    let [from, to] = [
      new Date(item.dropOffWindow[0]),
      new Date(item.dropOffWindow[1]),
    ];
    from = from.toLocaleString('en-AU', {timeStyle: 'short'});
    to = to.toLocaleString('en-AU', {timeStyle: 'short'});
    return (
      <Pressable
        onPress={() => selectTime(item)}
        key={item.id}
        style={[
          styles.timeTile,
          cartStore.selectedSlot?.id === item.id
            ? styles.timeTileSelected
            : null,
        ]}>
        <Text
          style={[
            styles.timeTileText,
            cartStore.selectedSlot?.id === item.id
              ? styles.timeTileTextSelected
              : null,
          ]}>
          {from} - {to}
        </Text>
      </Pressable>
    );
  };

  const renderTimes = () => {
    const times = cartStore.isPickup
      ? cartStore.pickupSlots[cartStore.selectedDay]
      : cartStore.deliverySlots[cartStore.selectedDay];
    if (
      (cartStore.isPickup && cartStore.loadingPickupSlots) ||
      (cartStore.isDelivery && cartStore.loadingDeliverySlots)
    ) {
      return <ActivityIndicator size="small" />;
    } else if (
      (cartStore.isPickup && !times?.length) ||
      (cartStore.isDelivery && !times?.length)
    ) {
      if (!cartStore.selectedDay) {
        return <Text style={styles.timeTileText}>Please select a day</Text>;
      }
      return (
        <Text style={styles.timeTileText}>
          No available {type.toLocaleLowerCase()} service available
        </Text>
      );
    } else {
      return (
        <View style={styles.timesContainer}>
          {times?.map(item => {
            return renderTimeTile(item);
          })}
        </View>
      );
    }
  };

  if (!open && cartStore.selectedSlot) {
    let [from, to] = [
      new Date(cartStore.selectedSlot?.dropOffWindow[0]),
      new Date(cartStore.selectedSlot?.dropOffWindow[1]),
    ];
    const day = from.toLocaleString('en-AU', {month: 'short', day: 'numeric'});
    from = from.toLocaleString('en-AU', {timeStyle: 'short'});
    to = to.toLocaleString('en-AU', {timeStyle: 'short'});

    return (
      <Pressable
        onPress={() => setOpen(true)}
        style={[styles.closedView, styles.row, styles.complete]}>
        <Text style={[styles.timeTileText, styles.complete]}>{type}</Text>
        <Text style={[styles.timeTileText, styles.complete]}>
          {day}, {from} - {to}
        </Text>
      </Pressable>
    );
  } else if (!open && !cartStore.selectedSlot) {
    return (
      <Button
        onPress={() => setOpen(true)}
        containerStyle={styles.selectTimeButton}
        title={`Select ${type.toLowerCase()} time`}
      />
    );
  } else {
    return (
      <View style={styles.main} testID="pickup-delivery-chooser">
        {renderDays()}
        {renderTimes()}
      </View>
    );
  }
});

export default TimeSlots;

const styles = StyleSheet.create({
  complete: {
    backgroundColor: theme.colour.uiControls.primary,
    color: 'white',
  },
  selectTimeButton: {
    marginTop: 8,
  },
  closedView: {
    borderWidth: 0.5,
    borderColor: theme.colour.border.general.primary,
    backgroundColor: 'white',
    borderRadius: 4,
    flex: 1,
    paddingVertical: 4,
    paddingHorizontal: 4,
    marginTop: 8,
  },
  timesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    flex: 1,
    justifyContent: 'space-between',
    marginLeft: -10,
    ...Platform.select({
      native: {
        display: 'flex',
      },
      default: {
        display: 'block',
      },
    }),
  },
  timeTile: {
    borderWidth: 0.5,
    borderColor: theme.colour.uiControls.primary,
    backgroundColor: 'white',
    borderRadius: 4,
    flex: 1,
    minWidth: '45%',
    alignItems: 'center',
    marginVertical: 8,
    marginLeft: 10,
  },
  timeTileSelected: {
    backgroundColor: theme.colour.background.general.primary,
  },
  timeTileText: {
    color: theme.colour,
    marginVertical: 8,
    marginHorizontal: 14,
    fontWeight: '500',
  },
  timeTileTextSelected: {
    color: 'white',
  },
  main: {
    backgroundColor: 'white',
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
  },
  dayTile: {
    flexDirection: 'column',
    borderWidth: 0.5,
    borderColor: theme.colour.uiControls.primary,
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 4,
    minWidth: 100,
    alignItems: 'center',
  },
  dayTileSelected: {
    backgroundColor: theme.colour.background.button.primary,
    color: 'white',
  },
  dayTileUpper: {
    color: theme.colour.text.heading.primary,
    fontWeight: '500',
  },
  dayTileLower: {
    color: theme.colour.text.heading.primary,
    fontWeight: '400',
    fontSize: 12,
  },
  daysRow: {
    maxHeight: 70,
  },
  verticalSpace: {
    marginVertical: 16,
  },
  horizontalSeparator: {
    marginHorizontal: 4,
  },
});
